import React, { useMemo } from "react";
import { Box, Card, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Printable, usePrintableContext } from "@components";
import { OKNTestResult } from "types/testTypes";
import {
  buildOKNGainSeries,
  buildOKNGainSummaryRows,
  buildOKNSegmentTraces,
  OKNGainSegmentSeries,
  OKNSegmentTrace,
} from "@utils/oknChartData";
import OKNEyePositionTraceChart from "@components/testContainer/common/OKNEyePositionTraceChart";
import OKNGainSeriesChart from "./OKNGainSeriesChart";
import OKNGainSummaryTable from "./OKNGainSummaryTable";

type OptokineticNystagmusTestEntryProps = {
  entry: OKNTestResult | null;
  header?: React.ReactNode;
};

// In the bordered report (prettier) layout each Printable.Section is one A4
// page; each segment contributes a row of two ~270px chart panels, so 4
// segments fit a page with room for the logo.
const TRACE_SEGMENTS_PER_PRINT_PAGE = 4;

function OptokineticNystagmusTestEntry({ entry, header }: OptokineticNystagmusTestEntryProps) {
  const { t } = useTranslation();
  const { prettier } = usePrintableContext();

  const results = entry?.results;
  const rawSamples = useMemo(() => results?.rawSamples ?? [], [results]);
  const oknGain = results?.oknGain ?? null;

  const summaryRows = useMemo(() => buildOKNGainSummaryRows(oknGain), [oknGain]);
  const traces = useMemo(() => buildOKNSegmentTraces(rawSamples), [rawSamples]);
  const gainSeries = useMemo(() => buildOKNGainSeries(oknGain, rawSamples), [oknGain, rawSamples]);
  const gainByIndex = useMemo(() => {
    const m = new Map<number, OKNGainSegmentSeries>();
    for (const s of gainSeries) m.set(s.segmentIndex, s);
    return m;
  }, [gainSeries]);

  const hasScorableGain = summaryRows.some((row) => row.leftMean != null || row.rightMean != null);

  const validityPct = useMemo(() => {
    const motion = rawSamples.filter((s) => String(s.phase).toLowerCase() !== "rest");
    if (!motion.length) return null;
    const valid = motion.filter((s) => s.isEyeTrackingValid === true).length;
    return (100 * valid) / motion.length;
  }, [rawSamples]);

  const rate = oknGain?.quality?.samplingRateHz ?? null;
  const NO_DATA = t("okn.tech.noData");

  if (!entry) {
    return (
      <Printable.Section>
        <Stack>
          {header}
          <Box mx={prettier ? 0 : "xl"}>
            <Text>{t("okn.noResults")}</Text>
          </Box>
        </Stack>
      </Printable.Section>
    );
  }

  const techCard = (
    <Card withBorder radius="md" p="sm">
      <Group gap="xl" wrap="wrap">
        <Text fz="xs">
          <b>{t("okn.tech.samplingRate")}</b>{" "}
          {rate != null
            ? t("okn.tech.samplingRateValue", {
                rate: rate.toFixed(1),
                isi: (1000 / rate).toFixed(2),
              })
            : NO_DATA}
        </Text>
        <Text fz="xs">
          <b>{t("okn.tech.validity")}</b>{" "}
          {validityPct != null
            ? t("okn.tech.validityValue", { combined: validityPct.toFixed(1) })
            : NO_DATA}
        </Text>
        <Text fz="xs">
          <b>{t("okn.tech.device")}</b> {entry?.device ?? NO_DATA}
        </Text>
        <Text fz="xs">
          <b>{t("okn.tech.build")}</b> {entry?.version ?? NO_DATA}
        </Text>
      </Group>
    </Card>
  );

  const summaryCard = (
    <Card withBorder radius="md" p="md">
      <Text fw={600} mb="sm">
        {t("okn.summaryTitle")}
      </Text>
      {oknGain == null ? (
        <Text c="dimmed" size="sm">
          {t("okn.gainNotProvided")}
        </Text>
      ) : summaryRows.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t("okn.noGainSegments")}
        </Text>
      ) : hasScorableGain ? (
        <OKNGainSummaryTable rows={summaryRows} />
      ) : (
        <Text c="dimmed" size="sm">
          {t("okn.noScorableGain")}
        </Text>
      )}
    </Card>
  );

  const tracePair = (trace: OKNSegmentTrace) => [
    <OKNEyePositionTraceChart key={`trace-${trace.segmentIndex}`} trace={trace} />,
    <OKNGainSeriesChart
      key={`gain-${trace.segmentIndex}`}
      series={
        gainByIndex.get(trace.segmentIndex) ?? {
          segmentIndex: trace.segmentIndex,
          axis: trace.axis,
          direction: trace.direction,
          speedDps: trace.speedDegPerSecond,
          points: [],
        }
      }
    />,
  ];

  const tracesTitle = (
    <Text fw={600} mb="sm">
      {t("okn.tracesTitle")}
    </Text>
  );

  if (!prettier) {
    return (
      <Printable.Section>
        <Stack>
          {header}
          <Box mx="xl">
            <Stack gap="lg">
              {techCard}
              {summaryCard}
              {traces.length > 0 && (
                <Card withBorder radius="md" p="md">
                  {tracesTitle}
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                    {traces.map(tracePair)}
                  </SimpleGrid>
                </Card>
              )}
            </Stack>
          </Box>
        </Stack>
      </Printable.Section>
    );
  }

  // Bordered report layout (test suite overviews): one A4 page per section so
  // page breaks never slice through a chart. Segment chart rows are chunked
  // into page-sized groups for the same reason.
  const traceChunks: OKNSegmentTrace[][] = [];
  for (let i = 0; i < traces.length; i += TRACE_SEGMENTS_PER_PRINT_PAGE) {
    traceChunks.push(traces.slice(i, i + TRACE_SEGMENTS_PER_PRINT_PAGE));
  }

  return (
    <>
      <Printable.Section>
        <Stack gap="lg">
          {header}
          {techCard}
          {summaryCard}
        </Stack>
      </Printable.Section>
      {traceChunks.map((chunk, chunkIndex) => (
        <Printable.Section key={`traces-${chunk[0].segmentIndex}`}>
          <Card withBorder radius="md" p="md">
            {chunkIndex === 0 && tracesTitle}
            <SimpleGrid cols={2} spacing="md">
              {chunk.map(tracePair)}
            </SimpleGrid>
          </Card>
        </Printable.Section>
      ))}
    </>
  );
}

OptokineticNystagmusTestEntry.selfSections = true;

export default OptokineticNystagmusTestEntry;
